import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useComments } from '../../hooks/useCommunity';
import { useAuth } from '../../context/AuthContext';

const MAX_BODY = 1000;

export default function CommentsSection({ couponId }) {
  const { user } = useAuth();
  const { comments, loading, post, remove, edit } = useComments(couponId);
  const [body, setBody]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await post(body.trim());
      setBody('');
    } catch (err) {
      setError(err.response?.data?.errors?.body?.[0] || err.response?.data?.error || 'Failed to post comment');
    } finally { setBusy(false); }
  };

  return (
    <section style={{ background: '#fff', borderTop: '1px solid #E4E7F0', padding: '56px 0 72px' }}>
      <div className="container">
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6B2FD9', marginBottom: 6 }}>Community</p>
          <h2 style={{ fontSize: 'clamp(22px, 2.6vw, 30px)', fontWeight: 800, letterSpacing: '-0.025em' }}>
            Comments {comments.length > 0 && <span style={{ color: '#8B94A8', fontWeight: 600 }}>· {comments.length}</span>}
          </h2>
        </div>

        {user ? (
          <form onSubmit={handleSubmit} style={{ marginBottom: 32 }}>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value.slice(0, MAX_BODY))}
              placeholder="Share what worked, when you used it, or tips for other shoppers."
              rows={3}
              disabled={busy}
              style={{
                width: '100%', padding: '14px 16px',
                border: '1.5px solid #E4E7F0', borderRadius: 12,
                fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ fontSize: 12, color: '#8B94A8' }}>{body.length}/{MAX_BODY}</span>
              <button type="submit" className="btn btn-primary" disabled={busy || !body.trim()}>
                {busy ? 'Posting…' : 'Post comment →'}
              </button>
            </div>
            {error && <p style={{ marginTop: 8, fontSize: 13, color: '#DC2626' }}>{error}</p>}
          </form>
        ) : (
          <div style={{ background: 'linear-gradient(135deg, #F3EEFF, #FFE8D6)', borderRadius: 14, padding: 20, marginBottom: 32, textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: '#0F1225', marginBottom: 12 }}>
              <strong>Sign in to share your experience</strong> — what worked for you helps other shoppers.
            </p>
            <Link to="/login" state={{ from: window.location.pathname }} className="btn btn-primary btn-sm">Sign in →</Link>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8B94A8' }}>Loading comments…</div>
        ) : comments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8B94A8', fontSize: 14 }}>
            No comments yet. Be the first to share what worked.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {comments.map(c => (
              <CommentRow
                key={c.id}
                comment={c}
                currentUserId={user?.id}
                onDelete={() => remove(c.id)}
                onEdit={(newBody) => edit(c.id, newBody)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CommentRow({ comment, currentUserId, onDelete, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(comment.body);
  const [busy, setBusy]       = useState(false);
  const isOwner = currentUserId && comment.user?.id === currentUserId;
  const isDeleted = comment.is_deleted;

  const saveEdit = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try { await onEdit(draft.trim()); setEditing(false); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ background: '#F7F8FC', borderRadius: 14, padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13 }}>
            {(comment.user?.name || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0F1225' }}>
              {comment.user?.name || 'Unknown'}
              {comment.user?.karma_points >= 10 && (
                <span title={`${comment.user.karma_points} karma`} style={{ marginLeft: 6, fontSize: 11, color: '#F97316' }}>
                  ⭐ {comment.user.karma_points}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#8B94A8' }}>
              {timeAgo(comment.created_at)}
              {comment.edited_at && <span style={{ fontStyle: 'italic' }}> · edited</span>}
            </div>
          </div>
        </div>
        {isOwner && !isDeleted && !editing && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setEditing(true)} style={iconBtn}>Edit</button>
            <button onClick={onDelete} style={{ ...iconBtn, color: '#DC2626' }}>Delete</button>
          </div>
        )}
      </div>

      {editing ? (
        <>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value.slice(0, MAX_BODY))}
            rows={3}
            style={{ width: '100%', padding: 10, border: '1.5px solid #E4E7F0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); setDraft(comment.body); }}>Cancel</button>
          </div>
        </>
      ) : (
        <p style={{ fontSize: 14, color: isDeleted ? '#8B94A8' : '#0F1225', lineHeight: 1.55, fontStyle: isDeleted ? 'italic' : 'normal' }}>
          {comment.body}
        </p>
      )}
    </div>
  );
}

const iconBtn = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontSize: 12, fontWeight: 600, color: '#4A5568', fontFamily: 'inherit',
  padding: '4px 8px', borderRadius: 6,
};

function timeAgo(iso) {
  if (!iso) return '';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return Math.floor(seconds / 60) + 'm ago';
  if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago';
  if (seconds < 604800) return Math.floor(seconds / 86400) + 'd ago';
  return new Date(iso).toLocaleDateString();
}
